// src/pages/PatientMapComp/PatientDetails/PatientUnifiedCardV2.jsx
import React, { useMemo, useState } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Box,
  Stack,
  Chip,
  Divider,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  CircularProgress,
  Avatar,
} from "@mui/material";

import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import CakeOutlinedIcon from "@mui/icons-material/CakeOutlined";
import WcOutlinedIcon from "@mui/icons-material/WcOutlined";

import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import HotelOutlinedIcon from "@mui/icons-material/HotelOutlined";
import ExitToAppOutlinedIcon from "@mui/icons-material/ExitToAppOutlined";
import CodeOutlinedIcon from "@mui/icons-material/CodeOutlined";
import HealthAndSafetyOutlinedIcon from "@mui/icons-material/HealthAndSafetyOutlined";

import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import MedicalServicesOutlinedIcon from "@mui/icons-material/MedicalServicesOutlined";
import DomainOutlinedIcon from "@mui/icons-material/DomainOutlined";

// ===================== Helpers =====================
const safeText = (v, fallback = "—") => (v === null || v === undefined || v === "" ? fallback : String(v));

function formatDob(dob) {
  if (!dob) return "—";
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return String(dob);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function toPercent(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  if (Number.isNaN(n)) return null;
  return n <= 1 ? Math.round(n * 100) : Math.round(n);
}

function shortAddress(candidate) {
  if (!candidate) return "—";
  const a1 = candidate.address1 || "";
  const a2 = candidate.address2 || "";
  const out = `${a1}${a1 && a2 ? " " : ""}${a2}`.trim();
  return out || "—";
}

// ===================== UI helpers =====================
function StatusChip({ active, label, color = "default", icon = null }) {
  const isOn = !(active === false || active === 0 || active === "0" || active === null || active === undefined);
  if (!isOn) return null;

  return <Chip size="small" variant="filled" color={color} icon={icon} label={label} sx={{ borderRadius: 2 }} />;
}

function InfoRow({ icon, label, value }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Box sx={{ width: 22, display: "flex", justifyContent: "center", opacity: 0.9 }}>{icon}</Box>
      <Typography variant="body2" sx={{ minWidth: 110, color: "text.secondary" }}>{label}</Typography>
      <Typography variant="body2" fontWeight={700} sx={{ wordBreak: "break-word" }}>{safeText(value)}</Typography>
    </Stack>
  );
}

export default function PatientUnifiedCardV2({
  // Core patient
  displayPatient,
  patientAge,

  // AdvancedMD match
  lookupLoading,
  lookupError,
  candidate,
  bestMatch,
  scorePercent,
  chartValue,
  resolvedDob,
  searchName,
  matchPayload,
  onRetry,

  // flags / assignment
  flags,
  assignment,

  // memberships
  membershipsPreview,
  membershipsMeta,
  onOpenMemberships,
  onOpenAdvancedMd,

  // last visit
  lastVisit,
  lastVisitLoading,

  // coding opportunities count (only show as chip in status section)
  codingOpsCount = 0,

  // care gaps count (only show as chip in status section)
  careGapsCount = 0,
}) {
  const [openAdvDialog, setOpenAdvDialog] = useState(false);
  const [openMemDialog, setOpenMemDialog] = useState(false);

  const name = displayPatient?.fullName ? displayPatient.fullName : safeText(searchName);
  const mrn = displayPatient?.mrn || displayPatient?.id || "—";
  const sex = displayPatient?.gender || displayPatient?.sex || "";

  const confidence = useMemo(() => {
    if (scorePercent !== null && scorePercent !== undefined) return scorePercent;

    const raw =
      bestMatch?.score ??
      bestMatch?.matchScore ??
      bestMatch?.MatchScore ??
      bestMatch?.probability ??
      bestMatch?.Probability ??
      candidate?.score ??
      null;

    return toPercent(raw);
  }, [scorePercent, bestMatch, candidate]);

  const advStatus = useMemo(() => {
    if (lookupLoading) return { label: "Matching…", color: "default" };
    if (lookupError) return { label: "Error", color: "error" };
    if (candidate) return { label: "Matched", color: "success" };
    return { label: "No match", color: "warning" };
  }, [lookupLoading, lookupError, candidate]);

  const membershipCount = membershipsMeta?.total ?? (Array.isArray(membershipsPreview) ? membershipsPreview.length : 0);
  const membershipActive = membershipsMeta?.activeCount ?? null;

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(String(text ?? ""));
    } catch (e) {
      console.warn("[PatientUnifiedCardV2] Clipboard not available:", e);
    }
  };

  const showAdvDetails = () => setOpenAdvDialog(true);
  const showMemDetails = () => setOpenMemDialog(true);

  const lastVisitDateText = lastVisit?.dateOfLastVisit ? formatDob(lastVisit.dateOfLastVisit) : "—";

  return (
    <Card variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
      <CardHeader
        avatar={
          <Avatar sx={{ bgcolor: "action.selected" }}>
            <PersonOutlineIcon />
          </Avatar>
        }
        title={<Typography variant="subtitle1" fontWeight={900} sx={{ lineHeight: 1.1 }}>{safeText(name)}</Typography>}
        subheader={
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
            <Chip size="small" icon={<BadgeOutlinedIcon />} label={`MRN: ${safeText(mrn)}`} variant="outlined" />
            {patientAge != null && <Chip size="small" label={`Age: ${patientAge}`} variant="outlined" />}
            {sex && <Chip size="small" icon={<WcOutlinedIcon />} label={`Sex: ${safeText(sex)}`} variant="outlined" />}
            <Chip size="small" icon={<CakeOutlinedIcon />} label={`DOB: ${formatDob(displayPatient?.dob || resolvedDob)}`} variant="outlined" />
          </Stack>
        }
        action={
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title="Copy MRN/ID">
              <IconButton size="small" onClick={() => handleCopy(mrn)}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {typeof onOpenAdvancedMd === "function" && (
              <Tooltip title="Open in AdvancedMD">
                <IconButton size="small" onClick={onOpenAdvancedMd}>
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            <Tooltip title="More info">
              <IconButton size="small" onClick={showAdvDetails}>
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        }
        sx={{ pb: 1.25 }}
      />

      <Divider />

      <CardContent>
        <Stack spacing={2}>
          {/* STATUS */}
          <Box>
            <Typography variant="overline" color="text.secondary">Status</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.75 }}>
              <StatusChip active={flags?.isHighRisk ?? flags?.highRisk} label="High Risk" color="warning" icon={<ReportProblemOutlinedIcon />} />
              <StatusChip active={flags?.isHighCost ?? flags?.highCost} label="High Cost" color="error" icon={<MonetizationOnOutlinedIcon />} />
              <StatusChip active={flags?.isEmergency ?? flags?.emergency} label="Emergency" color="error" icon={<WarningAmberOutlinedIcon />} />
              <StatusChip active={flags?.isAdmitted ?? flags?.admitted} label="Admitted" color="info" icon={<HotelOutlinedIcon />} />
              <StatusChip active={flags?.isDischarged ?? flags?.discharged} label="Discharged" color="default" icon={<ExitToAppOutlinedIcon />} />
              <StatusChip active={flags?.recentHospitalization} label="Recent hospitalization" color="warning" icon={<LocalHospitalOutlinedIcon />} />
              {codingOpsCount > 0 && (
                <Chip
                  size="small"
                  variant="filled"
                  color="secondary"
                  icon={<CodeOutlinedIcon />}
                  label={`${codingOpsCount} Coding Ops`}
                  sx={{ borderRadius: 2 }}
                />
              )}
              {careGapsCount > 0 && (
                <Chip
                  size="small"
                  variant="filled"
                  color="primary"
                  icon={<HealthAndSafetyOutlinedIcon />}
                  label={`${careGapsCount} Care Gaps`}
                  sx={{ borderRadius: 2 }}
                />
              )}
            </Stack>
          </Box>

          {/* ASSIGNMENT */}
          <Box>
            <Typography variant="overline" color="text.secondary">Assignment</Typography>
            <Stack spacing={0.75} sx={{ mt: 0.75 }}>
              <InfoRow icon={<MedicalServicesOutlinedIcon fontSize="small" />} label="PCP" value={assignment?.pcpName} />
              <InfoRow icon={<DomainOutlinedIcon fontSize="small" />} label="Location" value={assignment?.locationName} />
              <InfoRow icon={<BadgeOutlinedIcon fontSize="small" />} label="Member type" value={assignment?.memberType} />
              <InfoRow icon={<MonetizationOnOutlinedIcon fontSize="small" />} label="Payer/Plan" value={assignment?.payerPlan || displayPatient?.payer} />
            </Stack>
          </Box>

          {/* LAST VISIT */}
          <Box>
            <Divider />
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1 }}>
              <Typography variant="overline" color="text.secondary">Last visit</Typography>
              {lastVisitLoading ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="text.secondary">Loading…</Typography>
                </Stack>
              ) : null}
            </Stack>

            <Paper variant="outlined" sx={{ borderRadius: 2, p: 1.25, mt: 0.75 }}>
              {lastVisit ? (
                <Stack spacing={0.9}>
                  <InfoRow icon={<EventOutlinedIcon fontSize="small" />} label="Date" value={lastVisitDateText} />
                  <InfoRow icon={<AccessTimeOutlinedIcon fontSize="small" />} label="Since" value={lastVisit.timeSinceLastVisit || "—"} />
                  <InfoRow icon={<PlaceOutlinedIcon fontSize="small" />} label="Facility" value={lastVisit.facilityName || lastVisit.placeOfServiceDescription || lastVisit.medicalCenter || "—"} />
                  <InfoRow icon={<MedicalServicesOutlinedIcon fontSize="small" />} label="Provider" value={lastVisit.attendingPhysicianProvider || lastVisit.pcpAssigned || "—"} />
                  <InfoRow icon={<DomainOutlinedIcon fontSize="small" />} label="Status" value={lastVisit.insureStatus || lastVisit.status || "—"} />
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">No last-visit record found for this patient.</Typography>
              )}
            </Paper>
          </Box>

          {/* AdvancedMD */}
          <Box>
            <Divider />
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
              <Box>
                <Typography variant="subtitle2" fontWeight={900}>AdvancedMD</Typography>
                <Typography variant="caption" color="text.secondary">Match status & enrichment</Typography>
              </Box>

              <Chip
                size="small"
                label={confidence != null && candidate ? `${advStatus.label} · ${confidence}%` : advStatus.label}
                color={advStatus.color}
                variant={candidate ? "filled" : "outlined"}
              />
            </Stack>

            <Stack spacing={0.5} sx={{ mt: 1 }}>
              {candidate ? (
                <>
                  <Typography variant="body2"><strong>Chart:</strong> {safeText(candidate.chart || chartValue)}</Typography>
                  <Typography variant="body2" sx={{ wordBreak: "break-word" }}><strong>Email:</strong> {safeText(candidate.email)}</Typography>
                  <Typography variant="body2"><strong>Address:</strong> {safeText(shortAddress(candidate))}</Typography>

                  <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                    <Button size="small" variant="outlined" onClick={showAdvDetails}>View details</Button>
                  </Stack>
                </>
              ) : (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    {lookupError ? "AdvancedMD lookup failed." : "No AdvancedMD patient matched."}
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  <Button size="small" variant="outlined" onClick={onRetry} disabled={!matchPayload || lookupLoading}>
                    Retry
                  </Button>
                </Stack>
              )}
            </Stack>
          </Box>

          {/* Memberships */}
          <Box>
            <Divider />
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
              <Box>
                <Typography variant="subtitle2" fontWeight={900}>Memberships</Typography>
                <Typography variant="caption" color="text.secondary">Preview</Typography>
              </Box>

              <Stack direction="row" spacing={1} alignItems="center">
                {membershipActive != null && <Chip size="small" variant="outlined" label={`${membershipActive} active`} />}
                <Chip size="small" variant="outlined" label={`${membershipCount} total`} />
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    if (typeof onOpenMemberships === "function") onOpenMemberships();
                    else showMemDetails();
                  }}
                  disabled={membershipCount === 0}
                >
                  View details
                </Button>
              </Stack>
            </Stack>

            <Box sx={{ mt: 1 }}>
              {Array.isArray(membershipsPreview) && membershipsPreview.length > 0 ? (
                <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Plan</TableCell>
                        <TableCell>Effective</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {membershipsPreview.slice(0, 4).map((m, idx) => (
                        <TableRow key={idx}>
                          <TableCell sx={{ fontWeight: 800 }}>{safeText(m.planName || m.memberPlan || m.plan)}</TableCell>
                          <TableCell>{safeText(m.effectiveDate || m.effectiveDatePretty || m.memberPlanEffectiveDate)}</TableCell>
                          <TableCell>{safeText(m.status || m.statusText)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              ) : (
                <Typography variant="body2" color="text.secondary">No memberships loaded yet.</Typography>
              )}
            </Box>
          </Box>

        </Stack>

        {/* ===================== Dialog: AdvancedMD Details ===================== */}
        <Dialog open={openAdvDialog} onClose={() => setOpenAdvDialog(false)} fullWidth maxWidth="sm">
          <DialogTitle>AdvancedMD details</DialogTitle>
          <DialogContent dividers>
            {!candidate ? (
              <Typography variant="body2" color="text.secondary">No candidate available.</Typography>
            ) : (
              <Stack spacing={1}>
                <Typography variant="body2"><strong>Name:</strong> {safeText(candidate.name)}</Typography>
                <Typography variant="body2"><strong>Chart:</strong> {safeText(candidate.chart || chartValue)}</Typography>
                <Typography variant="body2"><strong>DOB:</strong> {safeText(candidate.dob || candidate.DOB || resolvedDob)}</Typography>
                <Typography variant="body2"><strong>Gender:</strong> {safeText(candidate.gender)}</Typography>
                <Typography variant="body2" sx={{ wordBreak: "break-word" }}><strong>Email:</strong> {safeText(candidate.email)}</Typography>
                <Typography variant="body2"><strong>Address:</strong> {safeText(shortAddress(candidate))}</Typography>
                {confidence != null && <Typography variant="body2"><strong>Confidence:</strong> {confidence}%</Typography>}
                {bestMatch?.message && <Typography variant="body2"><strong>Message:</strong> {safeText(bestMatch.message)}</Typography>}
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenAdvDialog(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* ===================== Dialog: Membership Details ===================== */}
        <Dialog open={openMemDialog} onClose={() => setOpenMemDialog(false)} fullWidth maxWidth="md">
          <DialogTitle>Memberships</DialogTitle>
          <DialogContent dividers>
            {Array.isArray(membershipsPreview) && membershipsPreview.length > 0 ? (
              <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Plan</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Effective</TableCell>
                      <TableCell>Location</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {membershipsPreview.map((m, idx) => (
                      <TableRow key={idx}>
                        <TableCell sx={{ fontWeight: 900 }}>
                          {safeText(m.planName || m.plan || m.payer || m.memberPlan)}
                        </TableCell>
                        <TableCell>{safeText(m.status || m.statusText)}</TableCell>
                        <TableCell>{safeText(m.effectiveDate || m.effectiveDatePretty || m.memberPlanEffectiveDate)}</TableCell>
                        <TableCell>{safeText(m.medicalCenter || m.officeLocation)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            ) : (
              <Typography variant="body2" color="text.secondary">No memberships loaded yet.</Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenMemDialog(false)}>Close</Button>
          </DialogActions>
        </Dialog>

      </CardContent>
    </Card>
  );
}
